// src/pages/Officer/OfficerReports.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Assessment,
  Download,
  DateRange,
  ShowChart,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';

const OfficerReports = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('week');
  const [reportData, setReportData] = useState({
    totalTrades: 145,
    winningTrades: 114,
    losingTrades: 31,
    winRate: 78.6,
    totalProfit: 24680.50,
    totalLoss: 6240.30,
    netProfit: 18440.20,
    avgWin: 216.49,
    avgLoss: 201.30,
    profitFactor: 3.95,
    largestWin: 1250.80,
    largestLoss: 890.50,
  });

  useEffect(() => {
    setTimeout(() => setLoading(false), 800);
  }, []);

  const MetricCard = ({ title, value, subtitle, icon, trend, color }) => (
    <Card
      sx={{
        height: '100%',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="start">
            <Box>
              <Typography
                variant="body2"
                sx={{ color: '#64748b', fontWeight: 600, mb: 1 }}
              >
                {title}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: color || '#1e293b',
                }}
              >
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${color || '#6366f1'}15 0%, ${
                  color || '#6366f1'
                }25 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          </Stack>

          {trend && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {trend > 0 ? (
                <TrendingUp sx={{ fontSize: 18, color: '#10b981' }} />
              ) : (
                <TrendingDown sx={{ fontSize: 18, color: '#ef4444' }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: trend > 0 ? '#10b981' : '#ef4444',
                  fontWeight: 600,
                }}
              >
                {Math.abs(trend)}% vs last period
              </Typography>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}
            >
              Performance Reports
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              Analyze your trading performance and metrics
            </Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
                startAdornment={
                  <DateRange sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} />
                }
              >
                <MenuItem value="day">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="quarter">This Quarter</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<Download />}
              sx={{
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#10b981',
                  color: '#10b981',
                },
              }}
            >
              Export
            </Button>
          </Stack>
        </Stack>

        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Net Profit"
              value={`$${reportData.netProfit.toLocaleString()}`}
              subtitle="After all trades"
              icon={<TrendingUp sx={{ color: '#10b981', fontSize: 24 }} />}
              trend={12.5}
              color="#10b981"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Win Rate"
              value={`${reportData.winRate}%`}
              subtitle={`${reportData.winningTrades}/${reportData.totalTrades} trades`}
              icon={<CheckCircle sx={{ color: '#6366f1', fontSize: 24 }} />}
              trend={3.2}
              color="#6366f1"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Profit Factor"
              value={reportData.profitFactor.toFixed(2)}
              subtitle="Risk/Reward Ratio"
              icon={<ShowChart sx={{ color: '#8b5cf6', fontSize: 24 }} />}
              trend={8.7}
              color="#8b5cf6"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              title="Total Trades"
              value={reportData.totalTrades}
              subtitle="Executed this period"
              icon={<Assessment sx={{ color: '#f59e0b', fontSize: 24 }} />}
              trend={-2.1}
              color="#f59e0b"
            />
          </Grid>
        </Grid>

        {/* Detailed Statistics */}
        <Grid container spacing={3}>
          {/* Profit & Loss Breakdown */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}
              >
                Profit & Loss Breakdown
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#64748b', fontWeight: 600 }}
                    >
                      Total Profit
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: '#10b981', fontWeight: 700 }}
                    >
                      ${reportData.totalProfit.toLocaleString()}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={100}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      background: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#64748b', fontWeight: 600 }}
                    >
                      Total Loss
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: '#ef4444', fontWeight: 700 }}
                    >
                      -${reportData.totalLoss.toLocaleString()}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={
                      (reportData.totalLoss / reportData.totalProfit) * 100
                    }
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      background: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                      },
                    }}
                  />
                </Box>

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ color: '#166534', fontWeight: 700 }}
                  >
                    Net Profit
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: '#10b981', fontWeight: 700 }}
                  >
                    ${reportData.netProfit.toLocaleString()}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Trade Statistics */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                border: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}
              >
                Trade Statistics
              </Typography>

              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Winning Trades
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                    {reportData.winningTrades}
                  </Typography>
                </Stack>

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Cancel sx={{ color: '#ef4444', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Losing Trades
                    </Typography>
                  </Stack>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>
                    {reportData.losingTrades}
                  </Typography>
                </Stack>

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1 }}
                >
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Average Win
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    ${reportData.avgWin.toFixed(2)}
                  </Typography>
                </Stack>

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1 }}
                >
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Average Loss
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    ${reportData.avgLoss.toFixed(2)}
                  </Typography>
                </Stack>

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1 }}
                >
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Largest Win
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: '#10b981' }}
                  >
                    ${reportData.largestWin.toFixed(2)}
                  </Typography>
                </Stack>

                <Divider />

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ py: 1 }}
                >
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Largest Loss
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: '#ef4444' }}
                  >
                    -${reportData.largestLoss.toFixed(2)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default OfficerReports;
